import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import { Checkbox } from '@/components/ui/catalyst/checkbox';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { blobSinkActionService } from '@/services/api/blobSinkActionService';
import { Blob, BlobSinkAction } from '@/services/types/blob-types';
import { getExternalSystemDisplayName } from '@/utils/externalSystemUtils';

import { formatDateString } from '../utils/fileUtils';

interface ApprovableAction {
  blobId: string;
  blobName: string;
  action: BlobSinkAction;
}

interface BulkApproveSyncDialogProps {
  isOpen: boolean;
  onClose: () => void;
  matterId: string;
  selectedBlobs: Blob[];
  onSuccess: () => void;
}

export const BulkApproveSyncDialog = ({
  isOpen,
  onClose,
  matterId,
  selectedBlobs,
  onSuccess,
}: BulkApproveSyncDialogProps) => {
  const [approveSinkAction] =
    blobSinkActionService.useBlobSinkActionsApproveMutation();

  // Collect all approvable actions
  const approvableActions: ApprovableAction[] = selectedBlobs.flatMap((blob) =>
    (blob.sink_actions ?? [])
      .filter((action) => action.status === 'AWAITING_APPROVAL')
      .map((action) => ({
        blobId: blob.id,
        blobName: blob.name,
        action,
      })),
  );

  const [checkedIds, setCheckedIds] = useState<Set<string>>(
    new Set(approvableActions.map((a) => a.action.id)),
  );
  const [isProcessing, setIsProcessing] = useState(false);

  const handleClose = () => {
    if (isProcessing) return;
    onClose();
  };

  const toggleAction = (actionId: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(actionId)) {
        next.delete(actionId);
      } else {
        next.add(actionId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (checkedIds.size === approvableActions.length) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(approvableActions.map((a) => a.action.id)));
    }
  };

  const handleApprove = async () => {
    const toApprove = approvableActions.filter((a) =>
      checkedIds.has(a.action.id),
    );
    if (toApprove.length === 0) return;

    setIsProcessing(true);
    try {
      const results = await Promise.allSettled(
        toApprove.map((a) =>
          approveSinkAction({
            matterId,
            blobId: a.blobId,
            actionId: a.action.id,
          }).unwrap(),
        ),
      );

      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.filter((r) => r.status === 'rejected').length;

      if (failed === 0) {
        toast.success(
          `Approved ${succeeded} doc sync action${succeeded > 1 ? 's' : ''}`,
        );
      } else {
        toast.error(
          `${succeeded} approved, ${failed} failed. Please retry the failed items.`,
        );
      }

      onSuccess();
      onClose();
    } catch {
      toast.error('Failed to approve doc sync actions');
    } finally {
      setIsProcessing(false);
    }
  };

  const checkedCount = checkedIds.size;
  const allChecked =
    approvableActions.length > 0 && checkedCount === approvableActions.length;

  return (
    <Dialog open={isOpen} onClose={handleClose} size="3xl">
      <div>
        <DialogTitle>Approve Doc Sync</DialogTitle>
        <DialogDescription>
          {approvableActions.length === 0
            ? 'No actions awaiting approval for the selected files.'
            : `${approvableActions.length} action${approvableActions.length > 1 ? 's' : ''} awaiting approval. Uncheck any you don\u2019t want to push.`}
        </DialogDescription>

        {approvableActions.length > 0 && (
          <DialogBody>
            <div className="max-h-96 overflow-y-auto rounded-md border border-zinc-200">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="w-10 px-3 py-2 text-left">
                      <Checkbox
                        checked={allChecked}
                        indeterminate={checkedCount > 0 && !allChecked}
                        onChange={toggleAll}
                      />
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-zinc-600">
                      File Name
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-zinc-600">
                      Destination
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-zinc-600">
                      System
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-zinc-600">
                      Confidence
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-zinc-600">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {approvableActions.map(({ blobName, action }) => (
                    <tr
                      key={action.id}
                      className="hover:bg-zinc-50 cursor-pointer"
                      onClick={() => toggleAction(action.id)}
                    >
                      <td className="px-3 py-2">
                        <Checkbox
                          checked={checkedIds.has(action.id)}
                          onChange={() => toggleAction(action.id)}
                        />
                      </td>
                      <td className="px-3 py-2 font-medium text-zinc-900 max-w-[200px] truncate">
                        {action.target_name || blobName}
                      </td>
                      <td className="px-3 py-2 text-zinc-600 max-w-[200px] truncate">
                        {action.target_path || '\u2014'}
                      </td>
                      <td className="px-3 py-2 text-zinc-600">
                        {getExternalSystemDisplayName(action.target_system)}
                      </td>
                      <td className="px-3 py-2 text-zinc-600">
                        {action.target_confidence != null
                          ? `${Math.round(action.target_confidence * 100)}%`
                          : '\u2014'}
                      </td>
                      <td className="px-3 py-2 text-zinc-600 whitespace-nowrap">
                        {formatDateString(action.created_at, 'short')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DialogBody>
        )}

        <DialogActions>
          <Button onClick={handleClose} disabled={isProcessing}>
            Cancel
          </Button>
          {approvableActions.length > 0 && (
            <Button
              onClick={handleApprove}
              disabled={isProcessing || checkedCount === 0}
              color="emerald"
            >
              {isProcessing
                ? 'Approving...'
                : `Approve${checkedCount > 0 ? ` (${checkedCount})` : ''}`}
            </Button>
          )}
        </DialogActions>
      </div>
    </Dialog>
  );
};
